import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Konva from "konva";
import { SidePanel } from "@shared/common/components/SidePanel";
import { Location } from "@opstel/query/models/location";
import { Action } from "@opstel/query/v2/models/action";
import { ShuntingUnit } from "@opstel/query/models/shuntingUnit";
import { EPDStyleProvider } from "../contexts/EPDStyle/EPDStyleProvider";
import { Checkbox } from "@shared/EDS/components/Checkbox";
import { Canvas } from "./Canvas";
import { TopMenu } from "./TopMenu";
import { BottomMenu } from "./BottomMenu";
import { useContainerDimensions } from "@shared/common/hooks/useContainerDimensions";
import { EPDContainer } from "./EPDContainer";
import { SCALING_VECTOR } from "../core/layoutConstants";
import { useZoomableCanvas } from "../contexts/ZoomableCanvas/useZoomableCanvas";

export interface EPDProps {
  readonly actions: Action[];
  readonly location: Location;
  readonly shuntingUnits: Record<string, ShuntingUnit>;
}

export function EPD({ actions, location, shuntingUnits }: EPDProps): React.JSX.Element {
  const stageRef = useRef<Konva.Stage>(null);
  const zoomToFit = useZoomableCanvas((s) => s.zoomToFit);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const toggleLabels = () => setShowLabels((old) => !old);
  const locationBoundingBox = useMemo(
    () => Location.calculateBoundingBox(location, SCALING_VECTOR),
    [location],
  );
  const [ref, dimensions] = useContainerDimensions<HTMLDivElement>();
  const handleZoomToFit = useCallback(
    () => stageRef.current && zoomToFit(stageRef.current, locationBoundingBox),
    [zoomToFit, locationBoundingBox],
  );

  useEffect(() => {
    handleZoomToFit();
  }, [handleZoomToFit, dimensions]);

  return (
    <EPDContainer ref={ref}>
      <EPDStyleProvider>
        {location && (
          <Canvas
            ref={stageRef}
            location={location}
            actions={actions}
            shuntingUnits={shuntingUnits}
            showLabels={showLabels}
            width={dimensions.width}
            height={dimensions.height}
          />
        )}
      </EPDStyleProvider>
      <TopMenu zoomToFit={handleZoomToFit} openSidePanel={() => setIsSidePanelOpen(true)} />
      <SidePanel title="Opties" isOpen={isSidePanelOpen} onClose={() => setIsSidePanelOpen(false)}>
        <div>
          <Checkbox isChecked={showLabels} onCheckedChange={toggleLabels} label="Toon labels" />
        </div>
      </SidePanel>
      <BottomMenu />
    </EPDContainer>
  );
}
